import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { API_URL } from '../config/api';
import './MonthlyDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface MonthlyData {
  month: string;
  collab: {
    views: number;
    spent: number;
    count: number;
  };
  signups: number;
  payments: number;
  uniquePayers: number;
  paymentAmount: number;
  totalViews: number;
  cpm: number;
  cac: number;
  ltv: number;
  signupConversionRate: number;
  paymentConversionRate: number;
}

interface Totals {
  collabViews: number;
  collabSpent: number;
  collabCount: number;
  signups: number;
  payments: number;
  uniquePayers: number;
  paymentAmount: number;
  totalViews: number;
  avgCpm: number;
  avgCac: number;
  avgLtv: number;
  avgSignupConversionRate: number;
  avgPaymentConversionRate: number;
}

interface HandleData {
  handle: string;
  count: number;
  amount: number;
}

interface MonthlyAttribution {
  influencer: { count: number; amount: number };
  unclassified: { count: number; amount: number };
  noHandle: { count: number; amount: number };
}

interface RevenueAttribution {
  summary: {
    total: { count: number; amount: number };
    surveyRespondents: { count: number; amount: number; percent: number };
    influencer: { count: number; amount: number; percent: number };
    unclassified: { count: number; amount: number; percent: number };
    noHandle: { count: number; amount: number; percent: number };
    ignored: { count: number; amount: number; percent: number };
  };
  monthly: Record<string, MonthlyAttribution>;
  allInfluencers: HandleData[];
  allUnclassified: HandleData[];
}

interface FunnelData {
  views: number;
  signups: number;
  payments: number;
  retention: {
    monthly: {
      avgDurationMonths: number;
      activeCount: number;
      totalCount: number;
    };
    yearly: {
      avgDurationYears: number;
      renewalRate: number;
      activeCount: number;
      totalCount: number;
      eligibleCount?: number;  // 1년 이상 된 구독 수
      underOneYearCount?: number;  // 1년 미만 구독 수
    };
    totalActive: number;
  };
  conversionRates: {
    viewToSignup: number;
    signupToPayment: number;
  };
}

interface DashboardData {
  monthlyStats: {
    monthly: MonthlyData[];
    totals: Totals;
  };
  revenueAttribution: RevenueAttribution;
  funnelData?: FunnelData;
}

const MonthlyDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('2025-01');
  const [endDate, setEndDate] = useState<string>('2025-12');
  const [selectedPreset, setSelectedPreset] = useState<string>('2025');

  // 프리셋 적용
  const applyPreset = (preset: string) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    switch (preset) {
      case 'thisMonth':
        setStartDate(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
        setEndDate(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
        break;
      case 'lastMonth': {
        const lastMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const lastMonthYear = currentMonth === 1 ? currentYear - 1 : currentYear;
        setStartDate(`${lastMonthYear}-${String(lastMonth).padStart(2, '0')}`);
        setEndDate(`${lastMonthYear}-${String(lastMonth).padStart(2, '0')}`);
        break;
      }
      case 'last3Months': {
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 2);
        setStartDate(`${threeMonthsAgo.getFullYear()}-${String(threeMonthsAgo.getMonth() + 1).padStart(2, '0')}`);
        setEndDate(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
        break;
      }
      case 'yearToDate':
        setStartDate(`${currentYear}-01`);
        setEndDate(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
        break;
      case 'allTime':
        setStartDate('2025-01');
        setEndDate(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
        break;
      default:
        // 특정 월 선택 (0-11)
        const monthIndex = parseInt(preset);
        if (!isNaN(monthIndex)) {
          setStartDate(`${currentYear}-${String(monthIndex + 1).padStart(2, '0')}`);
          setEndDate(`${currentYear}-${String(monthIndex + 1).padStart(2, '0')}`);
        }
    }
    setSelectedPreset(preset);
  };

  // 월 옵션 생성
  const getMonthOptions = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
    const options = [];
    for (let i = 0; i <= currentMonth; i++) {
      options.push({ label: months[i], value: i.toString() });
    }
    return options;
  };

  useEffect(() => {
    fetchDashboardData();
  }, [startDate, endDate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const url = `${API_URL}/api/stats/monthly-dashboard?${params.toString()}`;
      console.log('[MonthlyDashboard] Fetching from:', url);

      const dashboardResponse = await fetch(url);

      console.log('[MonthlyDashboard] Response status:', dashboardResponse.status);
      if (dashboardResponse.ok) {
        const result = await dashboardResponse.json();
        setData(result.data);
      } else {
        const text = await dashboardResponse.text();
        setError(`데이터 로드 실패: ${dashboardResponse.status} - ${text}`);
      }
    } catch (err) {
      console.error('[MonthlyDashboard] Error:', err);
      setError(`데이터 로드 에러: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    return Math.round(num).toLocaleString();
  };

  const formatCurrency = (num: number): string => {
    return '$' + Math.round(num).toLocaleString();
  };

  if (loading) {
    return (
      <div className="monthly-dashboard">
        <div className="loading">월별 대시보드 로딩 중...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="monthly-dashboard">
        <div className="error">{error || '데이터 없음'}</div>
      </div>
    );
  }

  const { monthlyStats, revenueAttribution } = data;
  const { monthly, totals } = monthlyStats;

  const labels = monthly.map(d => d.month);
  const collabViews = monthly.map(d => d.collab.views);
  const collabSpent = monthly.map(d => d.collab.spent);
  const totalViewsData = monthly.map(d => d.totalViews);
  const signups = monthly.map(d => d.signups);
  const payments = monthly.map(d => d.payments);
  const uniquePayers = monthly.map(d => d.uniquePayers);
  const signupConversionRates = monthly.map(d => d.signupConversionRate.toFixed(2));
  const paymentConversionRates = monthly.map(d => d.paymentConversionRate.toFixed(2));
  const cpmData = monthly.map(d => d.cpm.toFixed(2));

  // Collab CPM
  const collabCpm = totals.collabViews > 0
    ? ((totals.collabSpent / totals.collabViews) * 1000).toFixed(2)
    : '0.00';

  // CAC는 이제 백엔드에서 결제자 기준으로 계산됨
  const cac = totals.avgCac.toFixed(2);
  const ltv = totals.avgLtv.toFixed(2);

  // Chart options
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    }
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const }
    }
  };

  // Monthly attribution chart data
  const attrMonthLabels = Object.keys(revenueAttribution.monthly).sort();

  return (
    <div className="monthly-dashboard">
      <h1>Ssemble 월별 성과 대시보드</h1>
      <p className="subtitle">인플루언서 협업 + PostHog 제품 지표</p>

      {/* 날짜 범위 선택 */}
      <div className="date-controls">
        <div className="control-group">
          <label>시작:</label>
          <input
            type="month"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setSelectedPreset('');
            }}
            className="control-input"
          />
        </div>
        <div className="control-group">
          <label>종료:</label>
          <input
            type="month"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setSelectedPreset('');
            }}
            className="control-input"
          />
        </div>
      </div>

      {/* 인플루언서 협업 섹션 */}
      <div className="section-title">인플루언서 협업 성과</div>
      <div className="summary-cards five-columns">
        <div className="card views">
          <h3>총 조회수</h3>
          <div className="value">{formatNumber(totals.collabViews)}</div>
        </div>
        <div className="card spent">
          <h3>총 지출</h3>
          <div className="value">{formatCurrency(totals.collabSpent)}</div>
        </div>
        <div className="card count">
          <h3>총 협업 건수</h3>
          <div className="value">{totals.collabCount}</div>
        </div>
        <div className="card cpm">
          <h3>평균 CPM</h3>
          <div className="value">${collabCpm}</div>
        </div>
        <div className="card payment">
          <h3>서베이 ROI</h3>
          <div className="value">{totals.collabSpent > 0 ? ((revenueAttribution.summary.influencer.amount / totals.collabSpent) * 100).toFixed(0) : 0}%</div>
          <span className="metric-label">매출 {formatCurrency(revenueAttribution.summary.influencer.amount)}</span>
        </div>
      </div>

      <div className="chart-row">
        <div className="chart-container">
          <h2>월별 협업 지출</h2>
          <div className="chart-wrapper">
            <Bar
              data={{
                labels,
                datasets: [{
                  label: '지출 (USD)',
                  data: collabSpent,
                  backgroundColor: 'rgba(16, 185, 129, 0.7)',
                  borderColor: 'rgba(16, 185, 129, 1)',
                  borderWidth: 1
                }]
              }}
              options={barOptions}
            />
          </div>
        </div>
        <div className="chart-container">
          <h2>월별 협업 조회수</h2>
          <div className="chart-wrapper">
            <Bar
              data={{
                labels,
                datasets: [{
                  label: '조회수',
                  data: collabViews,
                  backgroundColor: 'rgba(59, 130, 246, 0.7)',
                  borderColor: 'rgba(59, 130, 246, 1)',
                  borderWidth: 1
                }]
              }}
              options={barOptions}
            />
          </div>
        </div>
      </div>

      <div className="chart-row">
        <div className="chart-container">
          <h2>월별 CPM 추이</h2>
          <div className="chart-wrapper">
            <Bar
              data={{
                labels,
                datasets: [{
                  label: 'CPM ($)',
                  data: cpmData,
                  backgroundColor: 'rgba(245, 158, 11, 0.7)',
                  borderColor: 'rgba(245, 158, 11, 1)',
                  borderWidth: 1
                }]
              }}
              options={barOptions}
            />
          </div>
        </div>
        <div className="chart-container">
          <h2>지출 대비 조회수 효율</h2>
          <div className="chart-wrapper">
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: '지출 ($K)',
                    data: collabSpent.map(s => s / 1000),
                    borderColor: 'rgba(16, 185, 129, 1)',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    fill: true,
                    tension: 0.3,
                    yAxisID: 'y'
                  },
                  {
                    label: '조회수 (M)',
                    data: collabViews.map(v => v / 1000000),
                    borderColor: 'rgba(59, 130, 246, 1)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    fill: true,
                    tension: 0.3,
                    yAxisID: 'y1'
                  }
                ]
              }}
              options={{
                ...lineOptions,
                scales: {
                  y: { type: 'linear', display: true, position: 'left', title: { display: true, text: '지출 ($K)' } },
                  y1: { type: 'linear', display: true, position: 'right', title: { display: true, text: '조회수 (M)' }, grid: { drawOnChartArea: false } }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* 제품 지표 섹션 */}
      <div className="section-title">제품 지표</div>
      <div className="summary-cards seven-columns">
        <div className="card views">
          <h3>총 조회수</h3>
          <div className="value">{formatNumber(totals.totalViews)}</div>
          <span className="metric-label">인플루언서 협업</span>
        </div>
        <div className="card signup">
          <h3>총 가입자 수</h3>
          <div className="value">{formatNumber(totals.signups)}</div>
          <span className="metric-label">가입 전환율: {totals.avgSignupConversionRate.toFixed(2)}%</span>
        </div>
        <div className="card payment">
          <h3>총 결제자 수</h3>
          <div className="value">{totals.uniquePayers.toLocaleString()}</div>
          <span className="metric-label">결제 전환율: {totals.avgPaymentConversionRate.toFixed(2)}%</span>
        </div>
        <div className="card count">
          <h3>조회수 → 결제자</h3>
          <div className="value">{totals.uniquePayers > 0 ? formatNumber(Math.round(totals.totalViews / totals.uniquePayers)) : '-'}</div>
          <span className="metric-label">조회 X당 결제자 1명</span>
        </div>
        <div className="card payment">
          <h3>RPM (조회 1K당 수익)</h3>
          <div className="value">${totals.totalViews > 0 ? (totals.paymentAmount / totals.totalViews * 1000).toFixed(2) : '0.00'}</div>
          <span className="metric-label">총매출 / 조회수 × 1000</span>
        </div>
        <div className="card cpm">
          <h3>CAC (유저당 비용)</h3>
          <div className="value">${cac}</div>
          <span className="metric-label">총비용 / 결제자</span>
        </div>
        <div className="card spent">
          <h3>LTV (유저당 매출)</h3>
          <div className="value">${ltv}</div>
          <span className="metric-label">총매출 / 결제자</span>
        </div>
      </div>

      <div className="chart-container">
        <h2>조회수 → 가입자 전환</h2>
        <div className="chart-wrapper">
          <Line
            data={{
              labels,
              datasets: [
                {
                  label: '총 조회수 (M)',
                  data: totalViewsData.map(v => v / 1000000),
                  borderColor: 'rgba(59, 130, 246, 1)',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  fill: true,
                  tension: 0.3,
                  yAxisID: 'y'
                },
                {
                  label: '가입자',
                  data: signups,
                  borderColor: 'rgba(236, 72, 153, 1)',
                  backgroundColor: 'rgba(236, 72, 153, 0.1)',
                  fill: true,
                  tension: 0.3,
                  yAxisID: 'y1'
                }
              ]
            }}
            options={{
              ...lineOptions,
              scales: {
                y: { type: 'linear', display: true, position: 'left', title: { display: true, text: '총 조회수 (M)' } },
                y1: { type: 'linear', display: true, position: 'right', title: { display: true, text: '가입자' }, grid: { drawOnChartArea: false } }
              }
            }}
          />
        </div>
      </div>

      <div className="chart-row">
        <div className="chart-container">
          <h2>가입자 → 결제자 전환</h2>
          <div className="chart-wrapper">
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: '가입자',
                    data: signups,
                    borderColor: 'rgba(236, 72, 153, 1)',
                    backgroundColor: 'rgba(236, 72, 153, 0.1)',
                    fill: true,
                    tension: 0.3,
                    yAxisID: 'y'
                  },
                  {
                    label: '결제자',
                    data: uniquePayers,
                    borderColor: 'rgba(20, 184, 166, 1)',
                    backgroundColor: 'rgba(20, 184, 166, 0.1)',
                    fill: true,
                    tension: 0.3,
                    yAxisID: 'y1'
                  }
                ]
              }}
              options={{
                ...lineOptions,
                scales: {
                  y: { type: 'linear', display: true, position: 'left', title: { display: true, text: '가입자' } },
                  y1: { type: 'linear', display: true, position: 'right', title: { display: true, text: '결제자' }, grid: { drawOnChartArea: false } }
                }
              }}
            />
          </div>
        </div>
        <div className="chart-container">
          <h2>월별 전환율 추이</h2>
          <div className="chart-wrapper">
            <Line
              data={{
                labels,
                datasets: [
                  {
                    label: '가입 전환율 (조회→가입, %)',
                    data: signupConversionRates,
                    borderColor: 'rgba(236, 72, 153, 1)',
                    backgroundColor: 'rgba(236, 72, 153, 0.2)',
                    fill: false,
                    tension: 0.3
                  },
                  {
                    label: '결제 전환율 (가입→결제, %)',
                    data: paymentConversionRates,
                    borderColor: 'rgba(16, 185, 129, 1)',
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    fill: false,
                    tension: 0.3
                  }
                ]
              }}
              options={barOptions}
            />
          </div>
        </div>
      </div>

      <div className="chart-row">
        <div className="chart-container">
          <h2>월별 가입자 수</h2>
          <div className="chart-wrapper">
            <Bar
              data={{
                labels,
                datasets: [{
                  label: '가입자',
                  data: signups,
                  backgroundColor: 'rgba(236, 72, 153, 0.7)',
                  borderColor: 'rgba(236, 72, 153, 1)',
                  borderWidth: 1
                }]
              }}
              options={barOptions}
            />
          </div>
        </div>
        <div className="chart-container">
          <h2>월별 결제자 수</h2>
          <div className="chart-wrapper">
            <Bar
              data={{
                labels,
                datasets: [{
                  label: '결제자',
                  data: uniquePayers,
                  backgroundColor: 'rgba(20, 184, 166, 0.7)',
                  borderColor: 'rgba(20, 184, 166, 1)',
                  borderWidth: 1
                }]
              }}
              options={barOptions}
            />
          </div>
        </div>
      </div>

      {/* 종합 테이블 */}
      <div className="chart-container">
        <h2>월별 종합 데이터</h2>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>월</th>
                <th>총조회수</th>
                <th>인플루언서</th>
                <th>총비용</th>
                <th>협업수</th>
                <th>가입자</th>
                <th>결제유저</th>
                <th>가입전환</th>
                <th>결제전환</th>
                <th>CPM</th>
                <th>CAC</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((d) => {
                const totalViews = d.collab.views;
                const totalSpent = d.collab.spent;
                const rowCpm = totalViews > 0 ? ((totalSpent / totalViews) * 1000).toFixed(2) : '-';
                const rowCac = d.uniquePayers > 0 ? (totalSpent / d.uniquePayers).toFixed(2) : '-';
                return (
                  <tr key={d.month}>
                    <td>{d.month}</td>
                    <td>{totalViews.toLocaleString()}</td>
                    <td>{d.collab.views.toLocaleString()}</td>
                    <td>${totalSpent.toLocaleString()}</td>
                    <td>{d.collab.count}</td>
                    <td>{d.signups.toLocaleString()}</td>
                    <td>{d.uniquePayers.toLocaleString()}</td>
                    <td className="conversion-rate">{d.signupConversionRate.toFixed(2)}%</td>
                    <td className="conversion-rate">{d.paymentConversionRate.toFixed(2)}%</td>
                    <td>${rowCpm}</td>
                    <td>${rowCac}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td>총계</td>
                <td>{totals.totalViews.toLocaleString()}</td>
                <td>{totals.collabViews.toLocaleString()}</td>
                <td>${totals.collabSpent.toLocaleString()}</td>
                <td>{totals.collabCount}</td>
                <td>{totals.signups.toLocaleString()}</td>
                <td>{totals.uniquePayers.toLocaleString()}</td>
                <td className="conversion-rate">{totals.avgSignupConversionRate.toFixed(2)}%</td>
                <td className="conversion-rate">{totals.avgPaymentConversionRate.toFixed(2)}%</td>
                <td>${totals.avgCpm.toFixed(2)}</td>
                <td>${totals.avgCac.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* 매출 귀속 분석 섹션 */}
      <div className="section-title">매출 귀속 분석 (Survey 핸들 기반)</div>
      <div className="summary-cards">
        <div className="card spent">
          <h3>총 매출</h3>
          <div className="value">{formatCurrency(revenueAttribution.summary.total.amount)}</div>
          <span className="metric-label">Stripe 성공 거래 ({revenueAttribution.summary.total.count.toLocaleString()}건)</span>
        </div>
        <div className="card signup">
          <h3>서베이 응답자 매출</h3>
          <div className="value">{formatCurrency(revenueAttribution.summary.surveyRespondents?.amount || 0)}</div>
          <span className="metric-label">{revenueAttribution.summary.surveyRespondents?.percent || 0}% ({(revenueAttribution.summary.surveyRespondents?.count || 0).toLocaleString()}건)</span>
        </div>
        <div className="card payment">
          <h3>인플루언서 (Collaboration)</h3>
          <div className="value">{formatCurrency(revenueAttribution.summary.influencer.amount)}</div>
          <span className="metric-label">{revenueAttribution.summary.influencer.percent}% ({revenueAttribution.summary.influencer.count.toLocaleString()}건)</span>
        </div>
        <div className="card cpm">
          <h3>미분류 핸들</h3>
          <div className="value">{formatCurrency(revenueAttribution.summary.unclassified.amount)}</div>
          <span className="metric-label">{revenueAttribution.summary.unclassified.percent}% ({revenueAttribution.summary.unclassified.count.toLocaleString()}건)</span>
        </div>
        <div className="card count">
          <h3>서베이 미응답</h3>
          <div className="value">{formatCurrency(revenueAttribution.summary.noHandle.amount)}</div>
          <span className="metric-label">{revenueAttribution.summary.noHandle.percent}% ({revenueAttribution.summary.noHandle.count.toLocaleString()}건)</span>
        </div>
      </div>

      <div className="chart-row">
        <div className="chart-container">
          <h2>전체 매출 귀속 비율</h2>
          <div className="chart-wrapper">
            <Doughnut
              data={{
                labels: ['인플루언서 (Collaboration)', '미분류 핸들', '서베이 미응답'],
                datasets: [{
                  data: [
                    revenueAttribution.summary.influencer.amount,
                    revenueAttribution.summary.unclassified.amount,
                    revenueAttribution.summary.noHandle.amount
                  ],
                  backgroundColor: ['rgba(59, 130, 246, 0.8)', 'rgba(245, 158, 11, 0.8)', 'rgba(156, 163, 175, 0.8)'],
                  borderColor: ['rgba(59, 130, 246, 1)', 'rgba(245, 158, 11, 1)', 'rgba(156, 163, 175, 1)'],
                  borderWidth: 2
                }]
              }}
              options={doughnutOptions}
            />
          </div>
        </div>
      </div>

      <div className="chart-container">
        <h2>월별 귀속 매출 추이</h2>
        <div className="chart-wrapper">
          <Bar
            data={{
              labels: attrMonthLabels,
              datasets: [
                {
                  label: '인플루언서',
                  data: attrMonthLabels.map(m => revenueAttribution.monthly[m]?.influencer.amount || 0),
                  backgroundColor: 'rgba(59, 130, 246, 0.8)',
                  borderColor: 'rgba(59, 130, 246, 1)',
                  borderWidth: 1
                }
              ]
            }}
            options={{
              ...barOptions,
              plugins: { legend: { position: 'top' } },
              scales: { x: { stacked: true }, y: { stacked: true } }
            }}
          />
        </div>
      </div>

      <div className="chart-row">
        <div className="chart-container scrollable">
          <h2>인플루언서 핸들별 매출</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>핸들</th>
                  <th>건수</th>
                  <th>매출</th>
                </tr>
              </thead>
              <tbody>
                {revenueAttribution.allInfluencers.map((item, idx) => (
                  <tr key={item.handle}>
                    <td>{idx + 1}</td>
                    <td>@{item.handle}</td>
                    <td>{item.count}</td>
                    <td>${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}><strong>총합</strong></td>
                  <td><strong>{revenueAttribution.allInfluencers.reduce((a, b) => a + b.count, 0)}</strong></td>
                  <td><strong>${revenueAttribution.allInfluencers.reduce((a, b) => a + b.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* 미분류 핸들 테이블 */}
      <div className="chart-row">
        {/* 미분류 핸들 테이블 */}
        <div className="chart-container scrollable">
          <h2>미분류 핸들별 매출 (귀속 불가 - 추가 분류 필요)</h2>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>핸들</th>
                  <th>건수</th>
                  <th>매출</th>
                </tr>
              </thead>
              <tbody>
                {revenueAttribution.allUnclassified.map((item, idx) => (
                  <tr key={item.handle}>
                    <td>{idx + 1}</td>
                    <td>@{item.handle}</td>
                    <td>{item.count}</td>
                    <td>${item.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}><strong>총합</strong></td>
                  <td><strong>{revenueAttribution.allUnclassified.reduce((a, b) => a + b.count, 0)}</strong></td>
                  <td><strong>${revenueAttribution.allUnclassified.reduce((a, b) => a + b.amount, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* 퍼널 분석 섹션 */}
      {data.funnelData && (
        <>
          <div className="section-title">퍼널 분석</div>
          <div className="funnel-container">
            {/* Views */}
            <div className="funnel-step views">
              <div className="step-icon">👀</div>
              <div className="step-value">{formatNumber(data.funnelData.views)}</div>
              <div className="step-label">Views</div>
              <div className="step-sublabel">총 조회수</div>
            </div>

            {/* Arrow: Views → Signup */}
            <div className="funnel-arrow">
              <div className="arrow-rate">{data.funnelData.conversionRates.viewToSignup.toFixed(2)}%</div>
              <div className="arrow-line">→</div>
            </div>

            {/* Signup */}
            <div className="funnel-step signup">
              <div className="step-icon">📝</div>
              <div className="step-value">{formatNumber(data.funnelData.signups)}</div>
              <div className="step-label">Signup</div>
              <div className="step-sublabel">가입자</div>
            </div>

            {/* Arrow: Signup → Payment */}
            <div className="funnel-arrow">
              <div className="arrow-rate">{data.funnelData.conversionRates.signupToPayment.toFixed(2)}%</div>
              <div className="arrow-line">→</div>
            </div>

            {/* Payment */}
            <div className="funnel-step payment">
              <div className="step-icon">💳</div>
              <div className="step-value">{formatNumber(data.funnelData.payments)}</div>
              <div className="step-label">Payment</div>
              <div className="step-sublabel">결제자</div>
            </div>

            {/* Arrow: Payment → Retention */}
            <div className="funnel-arrow">
              <div className="arrow-line">→</div>
            </div>

            {/* Retention */}
            <div className="funnel-step retention">
              <div className="step-icon">🔄</div>
              <div className="retention-details">
                <div className="retention-row">
                  <span className="plan-label">Monthly:</span>
                  <span className="plan-value">{data.funnelData.retention.monthly.avgDurationMonths}개월</span>
                </div>
                <div className="retention-row">
                  <span className="plan-label">Yearly:</span>
                  <span className="plan-value">{data.funnelData.retention.yearly.avgDurationYears}년</span>
                </div>
              </div>
              <div className="step-label">Retention</div>
              <div className="step-sublabel">활성 {data.funnelData.retention.totalActive.toLocaleString()}명</div>
            </div>
          </div>

          {/* 상세 리텐션 카드 */}
          <div className="retention-cards">
            <div className="retention-card monthly">
              <h4>Monthly Plan</h4>
              <div className="retention-stat">
                <span className="stat-value">{data.funnelData.retention.monthly.avgDurationMonths}</span>
                <span className="stat-unit">개월</span>
              </div>
              <div className="retention-info">평균 유지 기간</div>
              <div className="retention-counts">
                <span>활성: {data.funnelData.retention.monthly.activeCount.toLocaleString()}명</span>
                <span>전체: {data.funnelData.retention.monthly.totalCount.toLocaleString()}명</span>
              </div>
            </div>
            <div className="retention-card yearly">
              <h4>Yearly Plan</h4>
              <div className="retention-stat">
                <span className="stat-value">{data.funnelData.retention.yearly.avgDurationYears}</span>
                <span className="stat-unit">년</span>
              </div>
              <div className="retention-info">평균 유지 기간 (1년 이상 구독자 {data.funnelData.retention.yearly.eligibleCount?.toLocaleString() || 0}명 기준)</div>
              <div className="retention-counts">
                <span>활성: {data.funnelData.retention.yearly.activeCount.toLocaleString()}명</span>
                <span>갱신율: {data.funnelData.retention.yearly.renewalRate}%</span>
              </div>
              {(data.funnelData.retention.yearly.underOneYearCount ?? 0) > 0 && (
                <div className="retention-counts" style={{ marginTop: '4px', color: '#888' }}>
                  <span>1년 미만: {data.funnelData.retention.yearly.underOneYearCount?.toLocaleString()}명</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* 분석 결론 */}
      <div className="chart-container conclusion">
        <h2>분석 결론 ({new Date().toISOString().split('T')[0]})</h2>
        <div className="conclusion-content">
          <p><strong>1. 귀속 가능한 매출:</strong></p>
          <ul>
            <li>인플루언서 협업: <strong>{formatCurrency(revenueAttribution.summary.influencer.amount)}</strong> (전체 매출의 {revenueAttribution.summary.influencer.percent}%)</li>
          </ul>
          <p><strong>2. ROI:</strong></p>
          <ul>
            <li>인플루언서 지출: {formatCurrency(totals.collabSpent)} → 귀속 매출: {formatCurrency(revenueAttribution.summary.influencer.amount)} (ROI: {totals.collabSpent > 0 ? ((revenueAttribution.summary.influencer.amount / totals.collabSpent) * 100).toFixed(1) : '0'}%)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MonthlyDashboard;
